import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleLogin } from '@react-oauth/google';
import { API_BASE } from '../config';
import { loginSuccess, logout } from '../slices/authSlice';
import { cleanupGoogleSignInOverlays } from '../utils/googleSignInCleanup';
import { getPostLoginPath } from '../utils/postLoginRedirect';
import { fetchMemberDashboardSession, navigateAfterMemberLogin } from '../utils/memberPostLogin';
import { getStoredMemberToken, handleMemberSessionFailure } from '../utils/memberSession';
import { storeOtpDelivery } from '../utils/otpDeliveryMessage';
import SeoHead from '../components/SeoHead';
import { PAGE_SEO, SITE_NAME } from '../constants/seo';
import './Login.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const INTRO_SLIDES = [
  {
    title: 'Consult Health Specialists Anytime, Anywhere',
    subtitle: 'Consult top Health Specialists over Video, Voice or Chat anytime',
    illustration: 'consult',
  },
  {
    title: 'Lab Tests & Medicine Delivery at your doorstep',
    subtitle: 'Get Medicines Delivered and Lab Tests Done from the comfort of your home',
    illustration: 'delivery',
  },
  {
    title: 'Wellness workouts and Meditation podcasts',
    subtitle: 'Stay fit from home with wide range of Fitness Workout playlists and Mindfulness podcast',
    illustration: 'wellness',
  },
  {
    title: 'Hospitalisation Coverage and Reimbursements',
    subtitle: 'Cashless Hospitalisations and Fast Claim Reimbursements for the covered members',
    illustration: 'hospitalisation',
  },
  {
    title: 'Stay Motivated by Winning Rewards',
    subtitle: 'Win Fitcoins by completing challenges and tracking your fitness',
    illustration: 'rewards',
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UNAUTHORISED_MSG =
  'Account not set up. Contact your HR admin.';
const MEMBER_INACTIVE_MSG =
  'Your account is inactive. Contact your HR admin.';
const MEMBER_OFF_ROSTER_MSG =
  'Your employer access has ended. Contact your HR admin.';
const ROSTER_UNAVAILABLE_MSG =
  'Unable to verify your employer access right now. Please try again.';

function dispatchLoginAndRedirect({ dispatch, navigate, data, location }) {
  dispatch(
    loginSuccess({
      token: data.token,
      user: data.user,
      roles: data.roles || { isDoctor: false, isOps: false },
      authKind: data.authKind || 'member',
    })
  );
  navigateAfterMemberLogin(navigate, {
    needsProfileCompletion: data.needsProfileCompletion,
    needsMemberOnboarding: data.needsMemberOnboarding,
    roles: data.roles,
    from: location.state?.from,
    authKind: data.authKind || 'member',
  });
}

const Login = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('email');
  const [signInMethod, setSignInMethod] = useState('otp');
  const [memberStatus, setMemberStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const roles = useSelector((state) => state.auth.roles);
  const authKind = useSelector((state) => state.auth.authKind);
  const [googleError, setGoogleError] = useState('');
  const [sessionChecking, setSessionChecking] = useState(!!token);

  useEffect(() => {
    const storedToken = getStoredMemberToken(token);
    if (!storedToken) {
      setSessionChecking(false);
      return;
    }

    if (authKind === 'ops' || authKind === 'doctor') {
      navigate(getPostLoginPath(roles, location.state?.from, authKind), { replace: true });
      return;
    }

    let cancelled = false;
    setSessionChecking(true);

    fetchMemberDashboardSession(storedToken)
      .then(({ ok, status, data }) => {
        if (cancelled) return;
        if (!ok) {
          const outcome = handleMemberSessionFailure({
            status,
            data,
            dispatch,
            navigate,
          });
          if (outcome === 'retry') {
            dispatch(logout());
            setEmailError(
              data.error || 'Could not verify your session. Please sign in again.'
            );
            setCurrentSlide(INTRO_SLIDES.length);
          }
          return;
        }
        navigateAfterMemberLogin(navigate, {
          needsProfileCompletion: data.needsProfileCompletion,
          needsMemberOnboarding: data.needsMemberOnboarding,
          roles: data.roles || roles,
          from: location.state?.from,
          authKind: authKind || 'member',
        });
      })
      .finally(() => {
        if (!cancelled) setSessionChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, roles, authKind, navigate, location.state, dispatch]);

  useEffect(() => {
    const message = location.state?.loginMessage;
    if (!message) return;
    setEmailError(message);
    setCurrentSlide(INTRO_SLIDES.length);
    navigate('/login', { replace: true, state: { from: location.state?.from } });
  }, [location.state?.loginMessage, location.state?.from, navigate]);

  const emailTrimmed = email.trim().toLowerCase();
  const canConfirm = EMAIL_RE.test(emailTrimmed);

  const totalSlides = INTRO_SLIDES.length + 1;
  const isLoginSlide = currentSlide === INTRO_SLIDES.length;
  const isFirstSlide = currentSlide === 0;
  const isLastIntroSlide = currentSlide === INTRO_SLIDES.length - 1;

  const goNext = () => {
    if (currentSlide < totalSlides - 1) setCurrentSlide((s) => s + 1);
  };

  const goToLogin = () => {
    setCurrentSlide(INTRO_SLIDES.length);
  };

  const goBack = () => {
    if (currentSlide > 0) setCurrentSlide((s) => s - 1);
  };

  function resetAuthFlow() {
    setStep('email');
    setMemberStatus(null);
    setPassword('');
    setGoogleError('');
    setEmailError(null);
    sessionStorage.removeItem('auth_lookup_ok');
    sessionStorage.removeItem('auth_on_roster');
    sessionStorage.removeItem('auth_has_account');
  }

  function storeMemberLookupSession(status) {
    sessionStorage.setItem('auth_lookup_ok', '1');
    sessionStorage.setItem('auth_on_roster', status?.onRoster ? '1' : '0');
    sessionStorage.setItem('auth_has_account', status?.hasAccount ? '1' : '0');
  }

  function unauthorisedMessage(err) {
    if (err?.code === 'MEMBER_INACTIVE') return MEMBER_INACTIVE_MSG;
    if (err?.code === 'MEMBER_OFF_ROSTER') return MEMBER_OFF_ROSTER_MSG;
    if (err?.code === 'ROSTER_UNAVAILABLE') return ROSTER_UNAVAILABLE_MSG;
    if (err?.code === 'USER_NOT_PROVISIONED' || err?.code === 'NOT_ON_ALLOWLIST') {
      return UNAUTHORISED_MSG;
    }
    return null;
  }

  function otpSendErrorMessage(err) {
    const denied = unauthorisedMessage(err);
    if (denied) return denied;
    if (err?.status === 503) {
      return 'OTP could not be emailed. In development, check the API server log for the code, or ask your admin to configure email.';
    }
    return err?.message || 'Failed to send OTP.';
  }

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    const t = val.trim();
    if (t.length > 0 && !EMAIL_RE.test(t)) {
      setEmailError('Enter a valid email address');
    } else {
      setEmailError(null);
    }
  };

  async function lookupMember() {
    const res = await fetch(`${API_BASE}/api/auth/member-lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailTrimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(
        res.status === 504
          ? 'Gateway Timeout'
          : data.error || `Lookup failed (${res.status})`
      );
      err.code = data.code;
      err.status = res.status;
      throw err;
    }
    return data;
  }

  function loginLookupErrorMessage(err) {
    if (err?.status === 504 || /gateway timeout/i.test(err?.message || '')) {
      return 'Login is taking too long. Ensure the API server is running on port 5500 and try again.';
    }
    if (err?.message?.includes('Failed to fetch')) {
      return 'Cannot reach the server. Start the API (e.g. port 5500) or check your network.';
    }
    return err?.message || 'Server error. Please try again.';
  }

  async function sendOtp(mode) {
    const res = await fetch(`${API_BASE}/api/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailTrimmed, mode }),
    });
    let data = {};
    try {
      const text = await res.text();
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(
        !res.ok
          ? `Request failed (${res.status}).`
          : 'Invalid response from server.'
      );
    }
    if (!res.ok) {
      const err = new Error(data.error || data.message || `Failed to send OTP (HTTP ${res.status}).`);
      err.code = data.code;
      err.status = res.status;
      throw err;
    }
    sessionStorage.setItem('auth_email', emailTrimmed);
    sessionStorage.setItem('auth_otp_mode', mode || 'login');
    sessionStorage.setItem('auth_lookup_ok', '1');
    storeOtpDelivery(data.delivery, data.emailDeliveryFailed);
    navigate('/login/otp');
  }

  async function handleGoogleSuccess(credentialResponse) {
    setGoogleError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.existingUser || !data.token) {
        setGoogleError(
          data.error ||
            'Google Sign-In is only available for existing member accounts. Enter your work email to continue.'
        );
        return;
      }
      cleanupGoogleSignInOverlays();
      dispatchLoginAndRedirect({ dispatch, navigate, data, location });
    } catch {
      setGoogleError('Server error. Please try again or use email OTP.');
    }
  }

  async function handleContinue(e) {
    e.preventDefault();
    if (!canConfirm) return;

    setEmailError(null);
    setGoogleError('');
    setIsSubmitting(true);

    try {
      const status = await lookupMember();
      setMemberStatus(status);
      storeMemberLookupSession(status);

      setStep('signin-methods');
      setSignInMethod('otp');
    } catch (err) {
      const denied = unauthorisedMessage(err);
      if (denied) {
        setEmailError(denied);
      } else {
        setEmailError(loginLookupErrorMessage(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSendSignInOtp(e) {
    e.preventDefault();
    setEmailError(null);
    setIsSubmitting(true);
    try {
      await sendOtp('login');
    } catch (err) {
      setEmailError(otpSendErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordSignIn(e) {
    e.preventDefault();
    if (!password) {
      setEmailError('Enter your password.');
      return;
    }
    setEmailError(null);
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/member/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailTrimmed, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) {
        setEmailError(data.error || 'Sign in failed.');
        return;
      }
      dispatchLoginAndRedirect({ dispatch, navigate, data, location });
    } catch {
      setEmailError('Server error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const renderIllustration = (type) => (
    <div className="login-illustration">
      <svg
        viewBox="0 0 280 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="login-illustration-svg"
        aria-hidden="true"
      >
        <circle cx="50" cy="40" r="20" className="login-ill-circle" />
        <rect x="220" y="30" width="30" height="24" rx="4" className="login-ill-rect" />
        <path d="M30 160 L50 140 L70 160 L90 140 L110 160" stroke="currentColor" strokeWidth="2" fill="none" className="login-ill-line" />
        <g className="login-ill-person">
          <ellipse cx="140" cy="130" rx="22" ry="24" className="login-ill-head" />
          <path d="M118 155 L118 185 L125 185 L125 155 M155 155 L155 185 L162 185 L162 155" className="login-ill-legs" />
          <rect x="128" y="152" width="24" height="28" rx="4" className="login-ill-body" />
          <rect x="158" y="125" width="18" height="28" rx="3" className="login-ill-phone" />
          <circle cx="166" cy="132" r="4" className="login-ill-screen" />
          <path d="M175 138 L195 118 L205 128 L185 148 Z" className="login-ill-bubble" />
          <circle cx="190" cy="123" r="8" className="login-ill-doctor" />
          <path d="M188 123 L192 123 M190 121 L190 125" strokeWidth="1.5" className="login-ill-cross" />
        </g>
      </svg>
    </div>
  );

  if (sessionChecking) {
    return (
      <div className="login-page">
        <p style={{ textAlign: 'center', padding: '2rem' }}>Checking your session…</p>
      </div>
    );
  }

  return (
    <div className="login-page">
      <SeoHead {...PAGE_SEO.login} />
      <div className="login-bg-shapes" aria-hidden="true">
        <span className="login-shape login-shape-1"></span>
        <span className="login-shape login-shape-2"></span>
      </div>

      <Link to="/" className="login-logo">
        <img src="/images/unaffectedLogo.png" alt="" className="login-logo-image" />
        <span className="login-logo-text">{SITE_NAME}</span>
      </Link>

      <div className="login-card-wrap">
        <div className="login-card">
          {!isLoginSlide ? (
            <>
              <div className="login-card-shapes" aria-hidden="true">
                <span className="login-card-shape login-card-shape-1"></span>
                <span className="login-card-shape login-card-shape-2"></span>
                <span className="login-card-shape login-card-shape-3"></span>
              </div>

              <h1 className="login-title">{INTRO_SLIDES[currentSlide].title}</h1>
              {renderIllustration(INTRO_SLIDES[currentSlide].illustration)}
              <p className="login-subtitle">{INTRO_SLIDES[currentSlide].subtitle}</p>
            </>
          ) : (
            <>
              <h1 className="login-title login-title-form">
                {step === 'signin-methods' ? 'Sign in to your account' : 'Member login'}
              </h1>
              <p className="login-subtitle">
                {step === 'signin-methods'
                  ? 'Choose how you would like to sign in. OTP is not sent until you tap Send OTP.'
                  : 'Enter your work email. We will verify your account before sign-in.'}
              </p>

              {step === 'signin-methods' ? (
                <>
                  <p className="login-subtitle" style={{ marginBottom: '0.75rem' }}>
                    Signing in as <strong>{emailTrimmed}</strong>
                  </p>
                  {memberStatus?.onRoster && !memberStatus?.hasAccount && (
                    <p className="login-subtitle" style={{ marginBottom: '0.75rem' }}>
                      You are on your company roster. We will verify you by OTP.
                    </p>
                  )}

                  {GOOGLE_CLIENT_ID && (
                    <div className="login-google-wrap">
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setGoogleError('Google sign-in was cancelled or failed.')}
                        useOneTap={false}
                        ux_mode="popup"
                        text="continue_with"
                        shape="rectangular"
                        width="100%"
                      />
                      <p className="login-divider">
                        <span>or use email</span>
                      </p>
                    </div>
                  )}
                  {googleError && (
                    <p className="login-inline-error" role="alert" style={{ marginBottom: '0.75rem' }}>
                      {googleError}
                    </p>
                  )}

                  {memberStatus?.hasPassword ? (
                    <div className="login-mode-toggle" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                      <button
                        type="button"
                        className="login-btn-back"
                        style={{ flex: 1, opacity: signInMethod === 'otp' ? 1 : 0.7 }}
                        onClick={() => setSignInMethod('otp')}
                      >
                        OTP
                      </button>
                      <button
                        type="button"
                        className="login-btn-back"
                        style={{ flex: 1, opacity: signInMethod === 'password' ? 1 : 0.7 }}
                        onClick={() => setSignInMethod('password')}
                      >
                        Password
                      </button>
                    </div>
                  ) : null}

                  {!memberStatus?.hasPassword || signInMethod === 'otp' ? (
                    <form className="login-form" onSubmit={handleSendSignInOtp}>
                      <button type="submit" className="login-btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send OTP to email'}
                      </button>
                    </form>
                  ) : memberStatus?.hasPassword ? (
                    <form className="login-form" onSubmit={handlePasswordSignIn}>
                      <div className="form-group login-mobile-group">
                        <label htmlFor="login-password">Password</label>
                        <input
                          id="login-password"
                          type="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                        />
                      </div>
                      <button
                        type="submit"
                        className="login-btn-submit"
                        disabled={isSubmitting || !password}
                      >
                        {isSubmitting ? 'Signing in...' : 'Sign in with password'}
                      </button>
                    </form>
                  ) : null}

                  {emailError && (
                    <p className="login-inline-error" role="alert" style={{ marginTop: '0.75rem' }}>
                      {emailError}
                    </p>
                  )}

                  <button
                    type="button"
                    className="login-link-back"
                    style={{ marginTop: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={resetAuthFlow}
                  >
                    Use a different email
                  </button>
                </>
              ) : (
                <form className="login-form" onSubmit={handleContinue}>
                  <div className="form-group login-mobile-group">
                    <label htmlFor="login-email">Email</label>
                    <div className="login-mobile-input-wrap">
                      <input
                        id="login-email"
                        type="email"
                        inputMode="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder="you@example.com"
                        autoComplete="email"
                        className={emailError ? 'login-input-error' : ''}
                        aria-invalid={!!emailError}
                        aria-describedby={emailError ? 'login-email-error' : undefined}
                      />
                    </div>
                    {emailError && (
                      <p id="login-email-error" className="login-inline-error" role="alert">
                        {emailError}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="login-btn-submit"
                    disabled={!canConfirm || isSubmitting}
                  >
                    {isSubmitting ? 'Checking...' : 'Continue'}
                  </button>
                </form>
              )}
            </>
          )}

          <div className="login-pagination" aria-label="Slide navigation">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`login-dot ${i === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === currentSlide ? 'true' : undefined}
              />
            ))}
          </div>

          <div className="login-nav-buttons">
            <button
              type="button"
              className="login-btn-back"
              onClick={goBack}
              disabled={isFirstSlide}
              aria-label="Previous slide"
            >
              &larr; Back
            </button>
            {isLoginSlide ? (
              <Link to="/" className="login-link-back">
                Back to Home
              </Link>
            ) : isLastIntroSlide ? (
              <button
                type="button"
                className="login-btn-get-started"
                onClick={goToLogin}
                aria-label="Get started and go to login"
              >
                Get Started &rarr;
              </button>
            ) : (
              <button
                type="button"
                className="login-btn-next"
                onClick={goNext}
                aria-label="Next slide"
              >
                Next &rarr;
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
